import { Event, Person } from '@mui/icons-material';
import { Box } from '@mui/material';
import moment from 'moment';
import * as React from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useLoggedInEmployeeId } from '~/components/Auth';
import { EmployeeCalendar } from '~/components/EmployeeCalendar';
import { useAllOrgUnits } from '~/data/orgunits';
import {
  AnalyticsReportEmployeeScope,
  AnalyticsReportScope,
  AnalyticsReportTypes,
  useAbsencesReportLimitedData,
} from '~/data/reports';
import {
  ReportDatasetLayout,
  ReportDatasetRowCount,
  ReportDatasetSelector,
  ReportDateRangeSelector,
  ReportDateRangeSelectorMode,
  ReportViewLayout,
  useReportDataset,
  useReportNavigateToView,
} from '~/scenes/PortalModules/Analytics/common';
import { useAbsenceCalendarLimitedReport } from '~/scenes/PortalModules/Analytics/People/AbsenceCalendarLimitedReport/useAbsenceCalendarLimitedReport';
import { ShowEmployeesModeSelector } from '~/scenes/PortalModules/Analytics/People/AbsenceCalendarReport/ShowEmployeesModeSelector';

export const AbsenceCalendarLimitedReport = () => {
  const { t } = useTranslation();

  const { goToEmployeeBasicDataView } = useReportNavigateToView();

  const loggedInEmployeeId = useLoggedInEmployeeId();

  const { orgUnits } = useAllOrgUnits();
  const allOrgUnitIds = useMemo(() => {
    return orgUnits.map((unit) => unit.fOrgUnitId);
  }, [orgUnits]);

  const defaultReportSelection = useMemo(() => {
    return {
      reportType: {
        type: AnalyticsReportTypes.Unit,
        employeeId: loggedInEmployeeId,
        scope: AnalyticsReportScope.Direct,
      },
      params: {
        startDate: moment().subtract(1, 'month').toDate(),
        endDate: moment().add(2, 'months').toDate(),
        empScope: AnalyticsReportEmployeeScope.AllEmployees,
      },
    };
  }, [loggedInEmployeeId]);

  const reportDatasetProps = useReportDataset({
    selection: defaultReportSelection,
    config: {
      options: {
        types: [AnalyticsReportTypes.Unit],
        units: allOrgUnitIds,
      },
      enableUnitAccessToEmployeesOwnUnit: true,
      disableUnitOptionsValidation: true,
    },
  });

  const { reportRange } = useMemo(() => {
    const reportRange = {
      startDate:
        reportDatasetProps.reportSelection?.params?.startDate ??
        defaultReportSelection.params.startDate,
      endDate:
        reportDatasetProps.reportSelection?.params?.endDate ??
        defaultReportSelection.params.endDate,
    };

    return {
      reportRange,
    };
  }, [reportDatasetProps, defaultReportSelection]);

  const {
    data,
    isLoading: isLoadingReportData,
    failedToLoad,
  } = useAbsencesReportLimitedData(reportDatasetProps.reportSelection);

  const {
    showEmployeesModeSelector,
    counts,
    calendarProps,
    isLoading: isLoadingPublicHolidays,
  } = useAbsenceCalendarLimitedReport({
    data,
    ...reportRange,
  });

  return (
    <ReportViewLayout
      heading={t('laVacAbsCalendar')}
      slots={{
        toolbar: {
          main: (
            <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <ReportDatasetLayout showHeading={true}>
                <ReportDatasetSelector
                  {...reportDatasetProps}
                  isLoading={isLoadingReportData || isLoadingPublicHolidays}
                  failedToLoad={failedToLoad}
                  renderCurrentParams={({ params }) => {
                    return (
                      <>{`${moment(params?.startDate).format('L')} - ${moment(
                        params?.endDate,
                      ).format('L')}`}</>
                    );
                  }}
                  renderParamsFields={({ params, onChange }) => {
                    return (
                      <ReportDateRangeSelector
                        startDate={params.startDate}
                        endDate={params.endDate}
                        onRangeChange={(startDate, endDate) =>
                          onChange({
                            ...params,
                            startDate: startDate ?? params.startDate,
                            endDate: endDate ?? params.endDate,
                          })
                        }
                        mode={ReportDateRangeSelectorMode.DateRange}
                      />
                    );
                  }}
                />
                <ReportDatasetRowCount
                  count={counts.employees}
                  description={t('laTotalRecords')}
                  icon={<Person />}
                />
                <ReportDatasetRowCount
                  count={counts.absences}
                  description={t('laAbsences')}
                  icon={<Event />}
                />
              </ReportDatasetLayout>
              <ShowEmployeesModeSelector {...showEmployeesModeSelector} />
            </Box>
          ),
        },
      }}
    >
      <EmployeeCalendar
        containerWidth={500}
        startDate={reportRange.startDate}
        endDate={reportRange.endDate}
        {...calendarProps}
        onEmployeeClick={goToEmployeeBasicDataView}
      />
    </ReportViewLayout>
  );
};
